<template>
  <div>
    <loading
      :active.sync="loading"
      :can-cancel="false"
      background-color="#fff"
      :is-full-page="true"
    >
    </loading>
    <h2 v-if="!loading">Detalle servicios pagados - {{ userPayment.nombre }}</h2>
    <b-row v-if="!loading && total">
      <b-col lg="12">
        <div class="alert alert-info">
          <h4 class="alert-heading">
            <i class="fa fa-money"></i> Total del pago: {{ total | thousand }}
          </h4>
        </div>
      </b-col>
    </b-row>
    <b-row v-if="!loading && comentarios">
      <b-col lg="12">
        <div class="alert alert-light">
          <h5 class="alert-heading">
            <i class="fa fa-comment"></i> Comentarios del pago:
          </h5>
          <p class="mb-0">{{ comentarios }}</p>
        </div>
      </b-col>
    </b-row>
    <b-row v-if="errors.length > 0">
      <b-col lg="12">
        <div v-for="(error_message, iErrorMsg) in errors" v-bind:key="iErrorMsg">
          <CAlert color="danger" closeButton>{{ error_message }}</CAlert>
        </div>
      </b-col>
    </b-row>
    <section v-if="!loading">
      <b-row>
        <b-col lg="12">
          <CCard>
            <CCardHeader>
              <strong>Servicios pagados</strong>
              <div class="card-header-actions">
                <b-button @click="exportToExcel" class="ml-2">Exportar Excel</b-button>
                <b-button @click="goBack" variant="secondary" class="ml-2">Volver</b-button>
              </div>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol sm="12" lg="12" v-if="items.length > 0">
                  <b-table
                    :items="items"
                    :fields="fields"
                    :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc"
                    :sort-direction="sortDirection"
                    stacked="md"
                    show-empty
                    small
                    fixed
                    hover
                    bordered
                  >
                    <template #cell(estado)="data">
                      <b-badge
                        :variant="getStatusVariant(data.item.estado)"
                        :style="{ backgroundColor: getStatusColor(data.item.estado) }"
                      >
                        {{ getStatusLabel(data.item.estado) }}
                      </b-badge>
                    </template>

                    <template #cell(foto)="data">
                      <b-badge :variant="data.item.foto === 'SI' ? 'success' : 'secondary'">
                        {{ data.item.foto }}
                      </b-badge>
                    </template>

                    <template #cell(comentario)="data">
                      <b-badge :variant="data.item.comentario === 'SI' ? 'success' : 'secondary'">
                        {{ data.item.comentario }}
                      </b-badge>
                    </template>

                    <template #cell(gps_inicio)="data">
                      <b-badge :variant="data.item.gps_inicio === 'SI' ? 'success' : 'secondary'">
                        {{ data.item.gps_inicio }}
                      </b-badge>
                    </template>

                    <template #cell(distancia_cumplida)="data">
                      <b-badge :variant="data.item.distancia_cumplida === 'SI' ? 'success' : 'warning'">
                        {{ data.item.distancia_cumplida }}
                      </b-badge>
                    </template>

                    <template #cell(ganancia)="data">
                      <strong>{{ data.item.ganancia | thousand }}</strong>
                    </template>

                    <template #cell(nueva_ganancia)="data">
                      <strong v-if="data.item.nueva_ganancia > 0">{{ data.item.nueva_ganancia | thousand }}</strong>
                      <span v-else class="text-muted">-</span>
                    </template>
                  </b-table>
                </CCol>
                <CCol sm="12" lg="12" v-else>
                  <div class="text-center text-muted">
                    <p>No hay servicios pagados para mostrar</p>
                  </div>
                </CCol>
              </CRow>
              <CRow>
                <div>
                  <strong>Total servicios: {{ items.length }}</strong>
                </div>
              </CRow>
            </CCardBody>
          </CCard>
        </b-col>
      </b-row>
    </section>
  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import moment from "moment";
import Loading from "vue-loading-overlay";

export default {
  name: 'WalkerPaymentPaidDetail',
  components: {
    Loading
  },
  data() {
    return {
      loading: true,
      errors: [],
      userPayment: null,
      items: [],
      fields: [],
      total: null,
      comentarios: null,
      sortBy: '',
      sortDesc: false,
      sortDirection: "asc",
      statusOptions: [
        { value: 1, color: "#6A9D50", label: "Aprobado" },
        { value: 2, color: "#D65E81", label: "Rechazado" },
        { value: 3, color: "#D7CE55", label: "En análisis" },
        { value: 34, color: "#0089FF", label: "Comentarios" },
        { value: 35, color: "#51B685", label: "Aceptado" }
      ]
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    loadData() {
      this.loading = true
      this.userPayment = this.$store.getters.walkerPayPaidDetail
      
      if (!this.userPayment) {
        this.errors.push('No se encontró información del pago seleccionado')
        this.loading = false
        return
      }

      // Capturar comentarios del userPayment
      this.comentarios = this.userPayment.comentarios || null

      // Llamar a la función de Firebase
      const payload = {
        totalPayId: this.userPayment.id
      }

      const walkerPaymentsByTotalPayId = this.$firebase.functions().httpsCallable('walkerPaymentsByTotalPayId')
      
      walkerPaymentsByTotalPayId(payload).then(response => {
        const data = response.data
        this.fields = data.fields
        this.items = data.items
        this.total = data.total
        this.loading = false
      }).catch(error => {
        console.error('Error fetching paid services:', error)
        this.errors.push('Error al cargar los servicios pagados')
        this.loading = false
      })
    },
    exportToExcel() {
      // Preparar los datos para exportar, reemplazando el valor del estado por su label
      const itemsToExport = this.items.map(item => {
        const statusOption = this.statusOptions.find(opt => opt.value === item.estado);
        return {
          ...item,
          estado: statusOption ? statusOption.label : 'Desconocido'
        };
      });

      // Convertir los datos de la tabla a formato de hoja de cálculo
      const ws = XLSX.utils.json_to_sheet(itemsToExport);

      // Ajustar el ancho de las columnas
      const wscols = this.fields.map(() => ({ wch: 10 })); // Ancho uniforme para todas las columnas
      ws['!cols'] = wscols;

      // Crear un nuevo libro de trabajo
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Servicios Pagados');

      // Generar el archivo y descargarlo
      const joinName = this.userPayment.nombre.split(' ').join('_');
      const fileName = `servicios_pagados_${joinName}_${moment().format('YYYY-MMM')}.xlsx`;
      XLSX.writeFile(wb, fileName);
    },
    getStatusColor(status) {
      const option = this.statusOptions.find(opt => opt.value === status);
      return option ? option.color : this.statusOptions[2].color; // Default to "En análisis" color
    },
    getStatusVariant(status) {
      const option = this.statusOptions.find(opt => opt.value === status);
      if (!option) return 'secondary';
      
      switch (status) {
        case 1: return 'success';
        case 2: return 'danger';
        case 3: return 'warning';
        case 34: return 'info';
        case 35: return 'success';
        default: return 'secondary';
      }
    },
    getStatusLabel(status) {
      const option = this.statusOptions.find(opt => opt.value === status);
      return option ? option.label : 'Desconocido';
    },
    goBack() {
      this.$router.go(-1);
    }
  },
}
</script>

<style lang="scss" scoped>
.status-select {
  color: #333;
  font-weight: 500;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 4px 8px;
  min-width: 120px;
  
  &:focus {
    box-shadow: none;
    border-color: #ccc;
  }
  
  option {
    background-color: white;
    color: #333;
  }
}
</style>
